import { Button, ConfigProvider, Form, Select, Spin, Table } from 'antd';
import { LoadingCommon, SessionYearMonthSelect } from '@components';
import EmptyDataTable from '@components/EmptyDataTable/EmptyDataTable';
import { InventoryOutputConfirmDialog } from './components/InventoryOutputConfirmDialog';
import { getColumns } from './columns';
import { INVENTORY_REPORT_CONSTANT } from './constant';
import useInventoryReport from './hook/useInventoryReport';
import { InventoryReportWrapper } from './InventoryReportStyled';
import { useEffect, useRef } from 'react';

const { TITLE, LABEL, MESSAGE } = INVENTORY_REPORT_CONSTANT;

const columns = getColumns();

function InventoryReportPage() {
  const { state, handler } = useInventoryReport();
  const {
    isAdmin,
    periodOptions,
    storeOptions,
    selectedPeriod,
    selectedStore,
    currentPeriodOption,
    storeDisplayLabel,
    preview,
    allRows,
    loadingMore,
    loadingPage,
    loadingPreview,
    validationErrors,
    showOutputDialog,
    loadingOutput,
    isOutputEnabled,
    ctxStoreCode,
    ctxStoreName,
  } = state;

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  // stable ref so the scroll listener never needs to be re-attached just because loadMore changed
  const loadMoreRef = useRef(handler.loadMore);
  useEffect(() => {
    loadMoreRef.current = handler.loadMore;
  }, [handler.loadMore]);

  // Attach scroll listener once when table body first appears; re-attach after filter resets rows
  useEffect(() => {
    if (allRows.length === 0) {
      return;
    }
    const tableBody =
      tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-body');
    if (!tableBody) {
      return;
    }

    const handleScroll = () => {
      if (
        tableBody.scrollTop + tableBody.clientHeight >=
        tableBody.scrollHeight - 120
      ) {
        loadMoreRef.current();
      }
    };
    tableBody.addEventListener('scroll', handleScroll);
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, [allRows.length === 0]); // attach once when rows appear; re-attach when filter resets rows to empty

  const storeReadonlyText =
    `${ctxStoreCode ?? ''} ${ctxStoreName ?? ''}`.trim() || '—';

  return (
    <LoadingCommon loading={loadingPage}>
      <InventoryReportWrapper>
        {/* [3] Header: back button left, title centered */}
        <div className="inventory-report_header">
          <button
            className="inventory-report_back-btn"
            onClick={handler.handleBack}
          >
            {TITLE.BTN_BACK}
          </button>
          <h1>{TITLE.PAGE}</h1>
        </div>

        {/* [4][5] 出力条件 */}
        <div className="inventory-report_section">
          <div className="inventory-report_section-title">
            {LABEL.OUTPUT_CONDITIONS_SECTION}
          </div>
          <Form layout="vertical">
            <div className="inventory-report_conditions-grid">
              {/* [4] 棚卸年月 */}
              <Form.Item
                label={LABEL.INVENTORY_YEAR_MONTH}
                validateStatus={validationErrors.period ? 'error' : ''}
                help={validationErrors.period}
              >
                <SessionYearMonthSelect
                  sessions={periodOptions}
                  value={selectedPeriod}
                  onChange={handler.handleChangePeriod}
                />
              </Form.Item>

              {/* [5] 店舗 — Dropdown (管理者) / Text read-only (各店舗) */}
              <Form.Item
                label={LABEL.STORE}
                validateStatus={validationErrors.store ? 'error' : ''}
                help={validationErrors.store}
              >
                {isAdmin ? (
                  <Select
                    size="large"
                    value={selectedStore}
                    options={storeOptions}
                    onChange={handler.handleChangeStore}
                  />
                ) : (
                  <div className="inventory-report_store-readonly">
                    {storeReadonlyText}
                  </div>
                )}
              </Form.Item>
            </div>
          </Form>
        </div>

        {/* [6] 帳票プレビュー */}
        <div className="inventory-report_section">
          <div className="inventory-report_section-title">
            {LABEL.REPORT_PREVIEW_SECTION}
          </div>

          {/* [6.1] Report Header Note — static, bold, right-aligned */}
          <div className="inventory-report_header-note">
            {preview?.headerNote}
          </div>

          {/* [6.2] Report Table */}
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: 'var(--background-header-table)',
                  borderColor: 'var(--color-border-primary)',
                  headerColor: 'var(--color-header-table)',
                },
              },
            }}
            renderEmpty={() =>
              !loadingPreview && (!preview || preview.totalCount === 0) ? (
                <EmptyDataTable message={MESSAGE.I01} />
              ) : undefined
            }
          >
            <div ref={tableWrapperRef}>
              <Table
                rowKey={(record, index) =>
                  `${record.assetNo ?? 'row'}-${index}`
                }
                dataSource={allRows}
                columns={columns}
                loading={loadingPreview}
                pagination={false}
                bordered
                scroll={{ x: 1200, y: allRows.length > 0 ? 480 : undefined }}
              />
            </div>
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <Spin size="small" />
              </div>
            )}
          </ConfigProvider>
        </div>

        {/* [7][8] Footer buttons */}
        <div className="inventory-report_footer">
          <Button size="large" onClick={handler.handleBack}>
            {TITLE.BTN_BACK_FOOTER}
          </Button>
          <Button
            type="primary"
            size="large"
            disabled={!isOutputEnabled}
            onClick={handler.handleClickOutput}
          >
            {TITLE.BTN_OUTPUT}
          </Button>
        </div>

        {/* VAMS_PC_007_01 出力確認 dialog */}
        <InventoryOutputConfirmDialog
          open={showOutputDialog}
          loading={loadingOutput}
          periodDisplay={currentPeriodOption?.inventoryYearMonthDisplay ?? ''}
          storeDisplay={isAdmin ? storeDisplayLabel : storeReadonlyText}
          onCancel={handler.handleCancelOutputDialog}
          onOk={handler.handleConfirmOutput}
        />
      </InventoryReportWrapper>
    </LoadingCommon>
  );
}

export { InventoryReportPage };
